import { useMemo, useCallback } from 'react';

/**
 * useDirtyFields — shallow comparison of an edit form against the original
 * record to determine which fields have changed.
 *
 * Returns:
 *   - dirtyFields  — array of field names that differ
 *   - dirtyCount   — number of changed fields
 *   - isFieldDirty — (field) => boolean, for per-field visual markers
 *
 * Comparison is shallow: arrays and objects are compared via JSON.stringify,
 * primitives via strict equality. Keys that exist in editForm but not in
 * original (new fields) are counted as dirty. Null/undefined and empty
 * string are treated as equivalent (so clearing a field back to its
 * original empty value is not a false positive).
 *
 * @param {object} editForm  — the current editable form state
 * @param {object} original  — the originally loaded record
 */
export function useDirtyFields(editForm, original) {
    const dirtyFields = useMemo(() => {
        if (!editForm || !original) return [];
        const result = [];
        const keys = new Set([...Object.keys(editForm), ...Object.keys(original)]);
        for (const key of keys) {
            const newVal = editForm[key];
            const oldVal = original[key];
            // Treat null/undefined/'' as equivalent
            const norm = (v) => (v == null || v === '') ? '' : v;
            const a = norm(newVal);
            const b = norm(oldVal);
            if (typeof a === 'object' || typeof b === 'object') {
                if (JSON.stringify(a) !== JSON.stringify(b)) result.push(key);
            } else if (a !== b) {
                result.push(key);
            }
        }
        return result;
    }, [editForm, original]);

    const dirtyCount = dirtyFields.length;

    const isFieldDirty = useCallback(
        (field) => dirtyFields.includes(field),
        [dirtyFields]
    );

    return { dirtyFields, dirtyCount, isFieldDirty };
}
